// Bandeau de consentement cookies — conforme recommandations CNIL 2023.
// Règles clés :
//  - « Refuser » aussi visible et au même niveau que « Accepter »
//  - Aucun traceur non-essentiel avant clic (PostHog reste OFF)
//  - Poursuite de navigation ≠ consentement
//  - Consentement stocké 13 mois maximum, puis re-sollicitation

import { useEffect, useState } from"react";
import { Cookie, X } from"lucide-react";

const CONSENT_KEY ="keyto.cookieConsent.v1";
const MAX_AGE_MS = 13 * 30 * 24 * 60 * 60 * 1000; // 13 mois

export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.savedAt) return null;
    const age = Date.now() - new Date(parsed.savedAt).getTime();
    if (age > MAX_AGE_MS) return null; // expiré, re-solliciter
    return parsed;
  } catch (e) {
    console.warn("CookieBanner: corrupt consent, ignoring", e);
    return null;
  }
}

export default function CookieBanner({ onChangeView }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const c = getStoredConsent();
    if (!c) setShow(true);
  }, []);

  const persist = (analytics) => {
    const payload = { analytics, savedAt: new Date().toISOString() };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("CookieBanner: failed to persist consent", e);
    }
    // Opt-in / opt-out immédiat de PostHog si présent
    if (window.posthog && typeof window.posthog.opt_out_capturing ==="function") {
      if (analytics) window.posthog.opt_in_capturing();
      else window.posthog.opt_out_capturing();
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 md:p-4 pointer-events-none"
    >
      <div className="max-w-4xl mx-auto bg-white border border-keyto rounded-2xl shadow-2xl p-5 md:p-6 pointer-events-auto">
        <div className="flex items-start gap-3 md:gap-4">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-[#EBF1FB] flex items-center justify-center">
            <Cookie size={20} className="text-[#15294F]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="cookie-banner-title"
              className="font-heading font-bold text-base md:text-lg text-[#15294F]"
            >
              Vos préférences en matière de cookies
            </h2>
            <p
              id="cookie-banner-desc"
              className="mt-1 text-[13px] md:text-sm text-slate-600 leading-relaxed"
            >
              Nous utilisons des cookies <strong>strictement nécessaires</strong> au fonctionnement
              du site (toujours actifs) et, si vous l&apos;acceptez, un cookie de{" "}
              <strong>mesure d&apos;audience anonyme</strong> (PostHog) pour améliorer la
              pédagogie. Aucun traceur n&apos;est déposé avant votre choix. La poursuite de la
              navigation ne vaut pas consentement.
            </p>

            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                data-testid="cookie-accept"
                onClick={() => persist(true)}
                className="flex-1 px-4 py-2.5 bg-[#15294F] text-white text-sm font-bold rounded-md hover:bg-[#1F3B6B] transition"
              >
                Accepter
              </button>
              <button
                data-testid="cookie-reject"
                onClick={() => persist(false)}
                className="flex-1 px-4 py-2.5 bg-white border-2 border-[#15294F] text-[#15294F] text-sm font-bold rounded-md hover:bg-slate-50 transition"
              >
                Refuser
              </button>
              <button
                data-testid="cookie-customize"
                onClick={() => {
                  setShow(false);
                  onChangeView?.("cookies");
                }}
                className="px-4 py-2.5 bg-transparent text-[#15294F] text-sm font-semibold rounded-md hover:bg-slate-100 transition sm:flex-initial"
              >
                Personnaliser
              </button>
            </div>
          </div>
          <button
            data-testid="cookie-close"
            onClick={() => setShow(false)}
            aria-label="Fermer sans choisir (aucun cookie non essentiel ne sera déposé)"
            className="shrink-0 p-1 text-slate-400 hover:text-slate-700 transition"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
